import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'

import {
  MAT_COLOR_SWATCHES,
  BORDER_COLOR_SWATCHES,
  PAD_COLOR_SWATCHES,
  STITCHING_COLOR_SWATCHES
} from '../mock-data/floor-mat-colors';
import { images as imageGroup } from '../mock-data/floor-mat-images';
import { variants as variantsGroup } from '../mock-data/floor-mat-variants';
import { formatMoney, getMinAndMaxPriceFromVariants } from '../utils/helpers';

const productHandle = window?.conspire_product?.handle;
const images = imageGroup.find(g => g.handle === productHandle)?.images || imageGroup?.[0]?.images;
const variants = window?.conspire_product?.variants;

const TABS = [
  {
    name: 'Product Information',
    value: 'INFO',
    html: window?.conspire_product?.description
  },
  {
    name: 'Warranty',
    value: 'WARRANTY',
    html: window?.conspire_product_warranty
  },
  {
    name: 'FAQ',
    value: 'FAQ',
    html: window?.conspire_product_faq
  }
];

const imageMap = {
  "trunk": ["BB13", "BB14", "BB15", "BB16", "BB17", "BB19"],
  "2pcsFront": ["BB4", "BB5"],
  "2pcsRear": ["BB7", "BB8", "BB9", "BB10", "BB11", "BB12"],
  "4pcs": ["BB1", "BB2", "BB3"]
}

let selectedCar = {
  year: "",
  make: "",
  model: "",
  submodel: "",
  option: {}
};

try {
  selectedCar = {
    year: localStorage.getItem('fm-car-year') || "",
    make: JSON.parse(localStorage.getItem('fm-car-make')) || "",
    model: JSON.parse(localStorage.getItem('fm-car-model')) || "",
    submodel: localStorage.getItem('fm-car-submodel') || "",
    option: {}
  }
} catch (e) {
  console.log('error parsing selectedCar e', e);
}

const [minPrice, maxPrice] = getMinAndMaxPriceFromVariants(variants);

const INITIAL_STATE = {
  tabs: TABS,
  selectedMatColor: MAT_COLOR_SWATCHES[0],
  selectedBorderColor: BORDER_COLOR_SWATCHES[0],
  selectedPadColor: PAD_COLOR_SWATCHES[0],
  selectedStitchingColor: STITCHING_COLOR_SWATCHES[0],
  matColorSwatches: MAT_COLOR_SWATCHES,
  borderColorSwatches: BORDER_COLOR_SWATCHES,
  padColorSwatches: PAD_COLOR_SWATCHES,
  stitchingColorSwatches: STITCHING_COLOR_SWATCHES,
  baseImages: images?.baseImages,
  matImages: images?.matColorImages,
  padImages: images?.heelPadColorImages,
  borderImages: images?.borderColorImages,
  stichingImages: images?.stitchingColorImages,
  matOptions: [],
  selectedMatOptions: [],
  selectedMatAreas: {
    front: true,
    back: false,
    trunk: false,
  },
  minMaxPrices: `${formatMoney(minPrice / 100)} - ${formatMoney(maxPrice / 100)}`,
  selectedCar,
  quantity: 1,
  productVariant: {},
  total: 0,
};

export const useFloorMatStore = create(
  immer((set, get) => ({
    ...INITIAL_STATE,
    setSelectedMatColor: (color) =>
      set((state) => {
        state.selectedMatColor = color;
      }),
    setSelectedBorderColor: (color) =>
      set((state) => {
        state.selectedBorderColor = color;
      }),
    setSelectedPadColor: (color) =>
      set((state) => {
        state.selectedPadColor = color;
      }),
    setSelectedStitchingColor: (color) =>
      set((state) => {
        state.selectedStitchingColor = color;
      }),
    setSelectedCar: (car) =>
      set((state) => {
        state.selectedCar = car;
      }),
    setQuantity: (quantity) =>
      set((state) => {
        state.quantity = quantity;
      }),
    setProductVariant: (variant) =>
      set((state) => {
        state.productVariant = variant;
      }),
    setMatOptions: (options = []) => {
      set((state) => {
        let availableOptions = [];
        for (const option of options) {
          for (const variant of variants) {
            const partNumber = variant.sku.split('-')[2];
            if (partNumber === option.part_number) {
              const matchingPartNumbers = options.filter(o => o.part_number === option.part_number);
              let newOption = {
                ...variant,
                partNumber: option.part_number,
                isSelected: false,
                product_unique_list: option.product_unique_list,
                selectedProductUnique: option.product_unique_list.length ? option.product_unique_list[0] : ""
              };

              if (matchingPartNumbers.length > 1) {
                let partNumbers = [];
                for (let partNumber of matchingPartNumbers) {
                    partNumbers.push(partNumber.part_number_note)
                }
                newOption.part_number_note_list = partNumbers;
                newOption.selectedPartNumberNote = partNumbers[0];
            } else {
                newOption.part_number_note_list = [];
                newOption.selectedPartNumberNote = matchingPartNumbers[0].part_number_note;
            }

              availableOptions.push(newOption);
            }
          }
        }

        availableOptions.sort((a, b) => {
          const aPartNum = a?.partNumber?.split('BB')?.[1];
          const bPartNum = b?.partNumber?.split('BB')?.[1];

          return Number(aPartNum) - Number(bPartNum);
        });

        console.log('setMatOptions availableOptions', availableOptions);

        state.matOptions = availableOptions;
      });

      const matOptions = get().matOptions;

      if (matOptions?.length) {
        get().setSelectedMatOptions(matOptions[0]);
      }
    },
    setSelectedMatOptions: (option) => {
      set((state) => {
        if (!option) {
          state.selectedMatOptions = INITIAL_STATE.selectedMatOptions;

          return;
        }

        console.log('setSelectedMatOptions option', option);
        let selectedMatOption = state.matOptions.find(o => o.id === option.id);

        selectedMatOption.isSelected = !selectedMatOption.isSelected;

        if (imageMap["4pcs"].includes(option.partNumber)) {
          // 4pcs selected, unselect 2pcs front and rear if selected
          state.matOptions.filter(o => o.isSelected).map(o => {
            if (
              imageMap["2pcsFront"].includes(o.partNumber)
              || imageMap["2pcsRear"].includes(o.partNumber)
            ) {
                o.isSelected = false;
            }
          })
        }

        if (
          imageMap["2pcsFront"].includes(option.partNumber)
          || imageMap["2pcsRear"].includes(option.partNumber)
        ) {
          // 2pcs selected, unselect 4pcs if selected
          state.matOptions.filter(o => o.isSelected).map(o => {
            if (imageMap["4pcs"].includes(o.partNumber)){
                o.isSelected = false;
            }
          })
        }

        state.selectedMatOptions = state.matOptions.filter(o => o.isSelected);
      });

      const options = get().selectedMatOptions;

      get().setSelectedMatAreas(options);
      get().setTotal(options);
    },
    setSelectedMatAreas: (selectedOptions) =>
      set((state) => {
        if (!selectedOptions?.length) {
          state.selectedMatAreas = INITIAL_STATE.selectedMatAreas;
        }

        let areas = {
          front: false,
          back: false,
          trunk: false
        }

        for (const option of selectedOptions) {
          if (imageMap["4pcs"].includes(option.partNumber)) {
            areas.front = true;
            areas.back = true;
          }
          if (imageMap["2pcsFront"].includes(option.partNumber)) {
            areas.front = true;
          }
          if (imageMap["2pcsRear"].includes(option.partNumber)) {
            areas.back = true;
          }
          if (imageMap["trunk"].includes(option.partNumber)) {
            areas.trunk = true;
          }
        }

        if (selectedOptions?.length === 0) {
          areas.front = true;
        }

        state.selectedMatAreas = areas;
      }),
      setSelectedMatOptionUnique: (uniqueOption, option) =>
        set((state) => {
          console.log('setSelectedMatOptionUnique uniqueOption', uniqueOption);
          console.log('setSelectedMatOptionUnique option', option);
          const matOption = state.matOptions.find(o => o.id === option.id);
          const selectedOption = state.selectedMatOptions.find(o => o.id === option.id);

          matOption.selectedProductUnique = uniqueOption;

          if (selectedOption) {
            selectedOption.selectedProductUnique = uniqueOption;
          }
        }),
      setTotal: (selectedOptions) =>
        set((state) => {
          let total = 0

          for (const option of selectedOptions) {
            total += option.price;
          }

          state.total = total;
        }),
  }))
);
